import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import axios from "axios";

const InstagramOAuthPanel = ({ connectionId, config = {}, onConnectionChanged }) => {
    const [pipelines, setPipelines] = useState([]);
    const [pipelineId, setPipelineId] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const popupRef = useRef(null);

    const connected = Boolean(config.oauth_connected);
    const expiresAt = config.access_token_expires_at
        ? new Date(config.access_token_expires_at)
        : null;

    useEffect(() => {
        axios.get("/api/pipelines")
            .then(({ data }) => {
                const items = data.pipelines || [];
                setPipelines(items);
                const current = items.find(item => Number(item.id) === Number(config.pipeline_id));
                const fallback = current || items.find(item => item.is_default) || items[0];
                if (fallback) setPipelineId(String(fallback.id));
            })
            .catch(() => setMessage({ type: "danger", text: "Não foi possível carregar os pipelines." }));
    }, [config.pipeline_id]);

    useEffect(() => {
        const receiveOAuthResult = async (event) => {
            if (event.source !== popupRef.current) return;
            if (event.data?.type !== "fluxo-clinica:instagram-oauth") return;

            setBusy(false);
            setMessage({
                type: event.data.success ? "success" : "danger",
                text: event.data.message,
            });

            if (event.data.success) {
                await onConnectionChanged?.(event.data.connection_id);
            }
        };

        window.addEventListener("message", receiveOAuthResult);
        return () => window.removeEventListener("message", receiveOAuthResult);
    }, [onConnectionChanged]);

    const connect = async () => {
        setBusy(true);
        setMessage(null);

        try {
            const { data } = await axios.post("/api/connections/instagram/oauth/start", {
                pipeline_id: pipelineId || null,
                connection_id: connectionId || null,
            });
            const popup = window.open(
                data.authorization_url,
                "fluxo-clinica-instagram-oauth",
                "width=620,height=760,resizable=yes,scrollbars=yes"
            );

            if (!popup) {
                throw new Error("Permita pop-ups para conectar o Instagram.");
            }

            popupRef.current = popup;
            popup.focus();
        } catch (error) {
            setBusy(false);
            setMessage({
                type: "danger",
                text: error.response?.data?.message || error.message || "Não foi possível iniciar a conexão.",
            });
        }
    };

    const disconnect = async () => {
        if (!connectionId || !window.confirm("Desconectar esta conta do Instagram?")) return;

        setBusy(true);
        setMessage(null);
        try {
            await axios.post(`/api/connections/${connectionId}/instagram/disconnect`);
            setMessage({ type: "success", text: "Instagram desconectado." });
            await onConnectionChanged?.(connectionId);
        } catch (error) {
            setMessage({
                type: "danger",
                text: error.response?.data?.message || "Não foi possível desconectar o Instagram.",
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mb-4 rounded border p-3" style={{ background: "#fdf2f8", borderColor: "#f9a8d4" }}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                    <strong>{connected ? "Instagram conectado" : "Conecte sua conta profissional"}</strong>
                    <div className="small text-muted mt-1">
                        A autorização acontece diretamente na Meta. Senhas e tokens não são exibidos neste painel.
                    </div>
                </div>
                <span className={`badge ${connected ? "bg-success" : "bg-secondary"}`}>
                    {connected ? "Conectado" : "Não conectado"}
                </span>
            </div>

            {config.instagram_username && (
                <div className="small mb-2"><strong>Conta:</strong> @{config.instagram_username}</div>
            )}
            {expiresAt && !Number.isNaN(expiresAt.getTime()) && (
                <div className="small mb-3">
                    <strong>Token válido até:</strong> {expiresAt.toLocaleString("pt-BR")}
                </div>
            )}

            <Form.Group className="mb-3">
                <Form.Label>Pipeline para novos contatos</Form.Label>
                <Form.Select value={pipelineId} onChange={event => setPipelineId(event.target.value)}>
                    <option value="">Selecione...</option>
                    {pipelines.map(pipeline => (
                        <option key={pipeline.id} value={pipeline.id}>{pipeline.nome}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            {message && <Alert variant={message.type} className="py-2 small">{message.text}</Alert>}

            <div className="d-flex flex-wrap gap-2">
                <Button type="button" variant="danger" onClick={connect} disabled={busy || !pipelineId}>
                    {busy && <Spinner size="sm" className="me-2" />}
                    {connected ? "Reconectar" : "Conectar com Instagram"}
                </Button>
                {connected && (
                    <Button type="button" variant="outline-secondary" onClick={disconnect} disabled={busy}>
                        Desconectar
                    </Button>
                )}
            </div>
        </div>
    );
};

export default InstagramOAuthPanel;
